import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { AccessoryDto, AccessorySearchPaginatedDto } from "@/core/api/generated/v1";

export type AccessoryAutocompleteOption = BaseAutocompleteOption<AccessoryDto>;

export const getAccessoryOptionTitle = (data: AccessoryDto): string =>
  StringHelper.join(
    [
      data.name || data.localNumber,
      data.type && `(${enumService.getEnumValueName("AccessoryType", data.type)})`,
    ],
    ", ",
    {
      skipEmpty: true,
    },
  );

export const accessoryToOption = (data: AccessoryDto): AccessoryAutocompleteOption => ({
  id: data.id!,
  title: getAccessoryOptionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface AccessoryAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<AccessoryDto> {
  searchFilters?: Partial<Omit<AccessorySearchPaginatedDto, "search" | "includeIds">>;
}

export default function AccessoryAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: AccessoryAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={accessoryToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.accessoriesApi.apiV1AccessoriesSearchPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          accessorySearchPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          accessorySearchPaginatedDto: {
            ...params.accessorySearchPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Accessory'
      placeholder='Search for accessory...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='accessory' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option.data?.description}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
